// src/composables/usePushNotifications.js
// Manages the full push subscription lifecycle via a clean state machine.
//
// Status values:
//   'idle'          — not subscribed, no blocking conditions
//   'loading'       — async operation in progress
//   'subscribed'    — active browser push subscription confirmed
//   'denied'        — Notification.permission === 'denied'
//   'needs-install' — iOS device not running as standalone PWA
//   'not-available' — API unavailable (demo mode, no SW/PushManager support)
//
// IMPORTANT: isIOS and isStandalone are computed INSIDE usePushNotifications()
// (not at module level) so that Jest mocks applied before the call take effect.

import { ref } from 'vue';
import apiClient from '@/services/apiClient';

/**
 * Convert a base64url VAPID public key to a Uint8Array for pushManager.subscribe().
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from(rawData, (char) => char.charCodeAt(0));
}

/**
 * Encode an ArrayBuffer to a base64 string for sending subscription keys to the backend.
 */
function arrayBufferToBase64(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

export function usePushNotifications() {
  // Computed INSIDE the function so Jest mocks applied before calling work correctly
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isStandalone =
    navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;

  const status = ref('idle');

  /**
   * Called on mount — silently restores subscription state, never prompts the user.
   */
  async function checkExistingSubscription() {
    // 1. Demo mode guard — push not available for demo users
    if (localStorage.getItem('isDemoUser') === 'true') {
      status.value = 'not-available';
      return;
    }
    // 2. API availability guard — ServiceWorker and PushManager must both be present
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      status.value = 'not-available';
      return;
    }
    // 3. iOS install gate — iOS requires PWA to be installed before push subscription is possible
    if (isIOS && !isStandalone) {
      status.value = 'needs-install';
      return;
    }
    // 4. Permission denied — user has explicitly blocked notifications
    if (Notification.permission === 'denied') {
      status.value = 'denied';
      return;
    }
    // 5. Check for existing browser subscription (silent restore)
    try {
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.getSubscription();
      status.value = sub ? 'subscribed' : 'idle';
    } catch {
      status.value = 'not-available';
    }
  }

  /**
   * Called from an explicit user click — subscribes the browser and registers with the backend.
   * Must be called from a user gesture so Notification.requestPermission() is allowed.
   */
  async function subscribe() {
    if (status.value === 'loading') return;
    status.value = 'loading';
    try {
      // Fetch VAPID public key from backend
      const { data } = await apiClient.get('/api/v1/push/vapid-key/');
      const vapidKey = data.vapid_public_key;
      // Request notification permission (must be inside a user gesture)
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        status.value = 'denied';
        return;
      }
      // Subscribe via PushManager
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      // Send subscription to backend
      await apiClient.post('/api/v1/push/subscribe/', {
        endpoint: sub.endpoint,
        p256dh: arrayBufferToBase64(sub.getKey('p256dh')),
        auth: arrayBufferToBase64(sub.getKey('auth')),
      });
      status.value = 'subscribed';
    } catch {
      // If permission was denied mid-flow, reflect that; otherwise reset to idle
      status.value = Notification.permission === 'denied' ? 'denied' : 'idle';
    }
  }

  /**
   * Removes the browser push subscription and deletes it from the backend.
   */
  async function unsubscribe() {
    if (status.value === 'loading') return;
    status.value = 'loading';
    try {
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe(); // remove from browser
        await apiClient.delete('/api/v1/push/unsubscribe/', { data: { endpoint } });
      }
      status.value = 'idle';
    } catch {
      // Treat as unsubscribed on error — checkExistingSubscription will re-verify on next mount
      status.value = 'idle';
    }
  }

  return { status, checkExistingSubscription, subscribe, unsubscribe };
}
